import { observer } from 'mobx-react';

import { WIDGET_SAFE_ID_PREFIX } from '../../../../stores/event-trigger/constants';
import { isLimitedOfferBenefit, isWithinRewardClaimWindow } from '../../../../utils/events';
import { useEventTriggerWidgetData } from '../../hooks';
import type { EventTriggerWidgetDataType, WidgetSizeType } from '../../types';
import { GeneralSmallWidget, GeneralWideWidget } from '../GeneralWidget';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

import { EventTriggerWidgetTimer } from './EventTriggerWidgetTimer/EventTriggerWidgetTimer';
import { LimitedOfferSmallWidget } from './LimitedOfferSmallWidget/LimitedOfferSmallWidget';
import { LimitedOfferWideWidget } from './LimitedOfferWideWidget/LimitedOfferWideWidget';

interface EventTriggerWidgetProps {
  sizeType: WidgetSizeType;
  data: EventTriggerWidgetDataType;
}

function EventTriggerWidgetComponent(props: EventTriggerWidgetProps) {
  const { data, sizeType } = props;
  const { event } = data;

  const { widgetImageUrl, handleClick } = useEventTriggerWidgetData({ event });

  const hideWidgetTimer =
    !event.canDisplay ||
    event.eventTrigger.hideWidgetTimer ||
    isWithinRewardClaimWindow(event.expiredAt, event.rewardClaimEndDate);

  const timer = hideWidgetTimer ? null : <EventTriggerWidgetTimer event={event} extended={sizeType === 'wide'} />;
  const smallWidgetExtended = !!timer;
  // The wide limited offer hangs its stock tab above the widget, which the wrapper's own overflow
  // would otherwise cut off. The rail leaves enough room above a wide widget for the tab to show.
  const isLimitedOfferWide = sizeType === 'wide' && isLimitedOfferBenefit(event.benefit);

  // A limited offer brings its own small widget, the way the tournament widget does: the stock needs
  // a row above the timer, which is a layout of its own rather than a prop on the shared one. Decided
  // here and not in the Widget switch, which routes on widget type — a limited offer is still an offer.
  const renderWidget = () => {
    switch (true) {
      case isLimitedOfferBenefit(event.benefit):
        return sizeType === 'small' ? (
          <LimitedOfferSmallWidget event={event} timer={timer} widgetImageUrl={widgetImageUrl} />
        ) : (
          <LimitedOfferWideWidget event={event} timer={timer} widgetImageUrl={widgetImageUrl} />
        );
      default:
        return sizeType === 'small' ? (
          <GeneralSmallWidget timer={timer} widgetImageUrl={widgetImageUrl} />
        ) : (
          <GeneralWideWidget timer={timer} widgetImageUrl={widgetImageUrl} />
        );
    }
  };

  return (
    <WidgetWrapper
      dataTestId="event-trigger-widget"
      id={`${WIDGET_SAFE_ID_PREFIX}${event.userEventTriggerId}`}
      overflowVisible={isLimitedOfferWide}
      sizeType={sizeType}
      smallWidgetExtended={smallWidgetExtended}
      onClick={handleClick}
    >
      {renderWidget()}
    </WidgetWrapper>
  );
}

export const EventTriggerWidget = observer(EventTriggerWidgetComponent);
